import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Response, Document, Pagination } from '@ui/src';
import {
  AllowedCategory,
  DocumentsStats,
} from '../../../../new-application/types';
import {
  CustomerType,
  DocumentType,
  CustomerDocument,
  CustomerDocumentType,
  CustomerDocumentStatus,
  DocumentComment,
  CustomerAgreement,
  DocumentsState,
  DocumentsCategoryState,
} from '../../../types';
import { customersComponentApi } from '../../services/customers';
import { clientsDocumentsApi } from '../../../../../store/documents/services';

export interface DroppedDocument {
  fromType?: CustomerDocumentType;
  documentId: string;
  toType?: CustomerDocumentType;
  fromDocumentCategoryId?: string;
  toDocumentCategoryId?: string;
}

export interface CustomerDocumentsState {
  selectedDocumentsCount: number;
  searchText: string;
  allSelected: boolean;
  groupToMove?: DocumentType;
  blockView: boolean;
  droppedDocument?: DroppedDocument;
  deleteSuccess?: boolean;
  expiredDocuments?: CustomerDocument[];
  expiredDocumentsPagination?: Pagination;
  agreements?: CustomerAgreement[];
  usignedAgreements?: CustomerAgreement[];
  customerId?: string;
  customerType?: CustomerType;
  identificationDocumentsSelected: number;
  transactionDocumentsSelected: number;
  pspCustomerDocumentsSelected: number;
  cddCheckDocumentsSelected: number;
  otherDocumentsSelected: number;
  info?: CustomerDocument;
  documentId?: string;
  agreement?: CustomerAgreement;
  allowedCategories?: AllowedCategory[];
  stats?: DocumentsStats[];
  selectedDocumentsByGroupId: Record<string, number>;
  allDocuments?: DocumentsState;
  documentsByCategoryId?: DocumentsState;
  allDocumentsSelected: number;
  allDocumentsTotal: number;
  needToReloadCategoryId?: string;
  cursor?: {
    startCursor: string;
    endCursor: string;
    take: string;
  };
  cursorByCategoryId?: Record<
    string,
    {
      startCursor?: string;
      endCursor?: string;
      take?: string;
    }
  >;
  documentsByCategoryTotal: Record<string, number>;
}

const initialState: CustomerDocumentsState = {
  selectedDocumentsCount: 0,
  identificationDocumentsSelected: 0,
  transactionDocumentsSelected: 0,
  pspCustomerDocumentsSelected: 0,
  cddCheckDocumentsSelected: 0,
  otherDocumentsSelected: 0,
  allDocumentsSelected: 0,
  allDocumentsTotal: 0,
  documentsByCategoryTotal: {},
  allSelected: false,
  blockView: false,
  searchText: '',
  info: undefined,
  selectedDocumentsByGroupId: {},
};

export const customerDocumentsSlice = createSlice({
  name: 'customerDocuments',
  initialState,
  reducers: {
    setCursor: (
      state,
      action: PayloadAction<{
        startCursor: string;
        endCursor: string;
        take: string;
      }>,
    ) => {
      state.cursor = action.payload;
    },
    clearCursor: (state) => {
      state.cursor = undefined;
    },
    clearCursorByCategoryId: (state, action: PayloadAction<string>) => {
      const nextCursorByCategoryId = { ...state.cursorByCategoryId };

      if (nextCursorByCategoryId[action.payload]) {
        nextCursorByCategoryId[action.payload] = {};
      }

      state.cursorByCategoryId = nextCursorByCategoryId;
    },
    clearAllCursors: (state) => {
      state.cursor = undefined;
      state.cursorByCategoryId = undefined;
    },
    setCursorByCategoryId: (
      state,
      action: PayloadAction<{
        startCursor: string;
        endCursor: string;
        take: string;
        categoryId: string;
      }>,
    ) => {
      const nextCursorByCategoryId = { ...state.cursorByCategoryId };

      state.cursorByCategoryId = {
        ...nextCursorByCategoryId,
        [action.payload.categoryId]: {
          startCursor: action.payload.startCursor,
          endCursor: action.payload.endCursor,
          take: action.payload.take,
        },
      };
    },
    onSelectDocumentById: (
      state,
      action: PayloadAction<{
        documentCategoryId: string;
        selected: boolean;
        id: string;
      }>,
    ) => {
      const { documentCategoryId, selected, id } = action.payload;

      if (state.allDocuments) {
        const nextAllDocuments = { ...state.allDocuments };
        const nextDocumentsByCategoryId = { ...state.documentsByCategoryId };
        const nextSelectedDocumentsyCategoryId = {
          ...state.selectedDocumentsByGroupId,
        };

        const nextCategory = nextAllDocuments.all?.map((item) => {
          const nextItem = { ...item };

          const nextDocuments = nextItem.documents?.map((document) => {
            const result = { ...document };

            if (result.id === id) {
              result.isSelected = selected;
            }

            if (
              documentCategoryId === 'all' &&
              state.documentsByCategoryId &&
              result.documentCategoryId &&
              nextDocumentsByCategoryId[result.documentCategoryId]
            ) {
              let counter = 0;

              const nextCategoryById = nextDocumentsByCategoryId[
                result.documentCategoryId
              ]?.map((categoryById) => {
                const nextResult = { ...categoryById };

                const nextDocumentsInCategory = nextResult.documents?.map(
                  (documentInCategory) => {
                    const nextDocumentInCategory = { ...documentInCategory };

                    if (nextDocumentInCategory.id === id) {
                      nextDocumentInCategory.isSelected = selected;
                    }

                    return nextDocumentInCategory;
                  },
                );

                nextResult.documents = nextDocumentsInCategory;

                return nextResult;
              });

              nextCategoryById.forEach((categoryById) => {
                categoryById.documents?.forEach((documentInCategory) => {
                  if (documentInCategory.isSelected) {
                    counter += 1;
                  }
                });
              });

              nextDocumentsByCategoryId[result.documentCategoryId] =
                nextCategoryById;

              nextSelectedDocumentsyCategoryId[result.documentCategoryId] =
                counter;
            }

            return result;
          });

          nextItem.documents = nextDocuments;

          return nextItem;
        });

        nextAllDocuments.all = nextCategory;

        let counterAll = 0;

        nextCategory?.forEach((item) => {
          item.documents?.forEach((document) => {
            if (document.isSelected) {
              counterAll += 1;
            }
          });
        });

        if (documentCategoryId === 'all') {
          state.documentsByCategoryId = nextDocumentsByCategoryId;
          state.selectedDocumentsByGroupId = nextSelectedDocumentsyCategoryId;
        }

        state.allDocuments = nextAllDocuments;
        state.allDocumentsSelected = counterAll;
      }

      if (
        documentCategoryId !== 'all' &&
        state.documentsByCategoryId &&
        state.documentsByCategoryId[documentCategoryId]
      ) {
        const nextDocumentsByCategoryId = { ...state.documentsByCategoryId };

        const nextCategory = nextDocumentsByCategoryId[documentCategoryId]?.map(
          (item) => {
            const result = { ...item };

            const nextDocuments = result?.documents?.map((document) => {
              const nextDocument = { ...document };

              if (nextDocument.id === id) {
                nextDocument.isSelected = selected;
              }

              return nextDocument;
            });

            result.documents = nextDocuments;

            return result;
          },
        );

        let categoryCounter = 0;

        nextCategory?.forEach((item) => {
          item.documents?.forEach((document) => {
            if (document.isSelected) {
              categoryCounter += 1;
            }
          });
        });

        nextDocumentsByCategoryId[documentCategoryId] = nextCategory;

        const nextSelectedDocumentsyCategoryId = {
          ...state.selectedDocumentsByGroupId,
        };

        nextSelectedDocumentsyCategoryId[documentCategoryId] = categoryCounter;

        state.documentsByCategoryId = nextDocumentsByCategoryId;
        state.selectedDocumentsByGroupId = nextSelectedDocumentsyCategoryId;
      }
    },
    onSelectDocument: (
      state,
      action: PayloadAction<{
        type: string;
        selected: boolean;
        id: string;
      }>,
    ) => {
      const { type, selected, id } = action.payload;

      if (state.allDocuments) {
        const nextAllDocuments = { ...state.allDocuments };
        const nextCategory = nextAllDocuments.all?.map((item) => {
          const nextItem = { ...item };

          const nextDocuments = nextItem.documents?.map((document) => {
            const result = { ...document };

            if (result.id === id) {
              result.isSelected = selected;
            }

            return result;
          });

          nextItem.documents = nextDocuments;

          return nextItem;
        });

        nextAllDocuments.all = nextCategory;

        let counterAll = 0;

        nextCategory?.forEach((item) => {
          item.documents?.forEach((document) => {
            if (document.isSelected) {
              counterAll += 1;
            }
          });
        });

        state.allDocuments = nextAllDocuments;
        state.allDocumentsSelected = counterAll;
      }

      if (type !== 'all' && state.documentsByCategoryId) {
        const currentCategory = state.allowedCategories?.find(
          (item) => item.category === type || item.id === type,
        );

        if (currentCategory) {
          const nextDocumentsByCategoryId = { ...state.documentsByCategoryId };

          const nextCategory = nextDocumentsByCategoryId[
            currentCategory.id
          ]?.map((item) => {
            const result = { ...item };

            const nextDocuments = result?.documents?.map((document) => {
              const nextDocument = { ...document };

              if (nextDocument.id === id) {
                nextDocument.isSelected = selected;
              }

              return nextDocument;
            });

            result.documents = nextDocuments;

            return result;
          });

          let categoryCounter = 0;

          nextCategory?.forEach((item) => {
            item.documents?.forEach((document) => {
              if (document.isSelected) {
                categoryCounter += 1;
              }
            });
          });

          nextDocumentsByCategoryId[currentCategory.id] = nextCategory;

          const nextSelectedDocumentsyCategoryId = {
            ...state.selectedDocumentsByGroupId,
          };

          nextSelectedDocumentsyCategoryId[currentCategory.id] =
            categoryCounter;

          state.documentsByCategoryId = nextDocumentsByCategoryId;
          state.selectedDocumentsByGroupId = nextSelectedDocumentsyCategoryId;
        }
      }
    },
    clearSelectedState: (state) => {
      const nextAllDocumentsState = { ...state.allDocuments };

      const nextAllDocuments = nextAllDocumentsState?.all?.map((item) => {
        const result = { ...item };

        const nextDocuments = result.documents?.map((document) => {
          const nextDocument = { ...document };

          nextDocument.isSelected = false;

          return nextDocument;
        });

        result.documents = nextDocuments;

        return result;
      });

      nextAllDocumentsState.all = nextAllDocuments;

      const nextDocumentsByCategoryId = { ...state.documentsByCategoryId };

      state.allowedCategories?.forEach((item) => {
        if (nextDocumentsByCategoryId[item.id]) {
          const nextCategory = nextDocumentsByCategoryId[item.id]?.map(
            (category) => {
              const result = { ...category };

              const nextDocuments = result.documents?.map((document) => {
                const nextDocument = { ...document };

                nextDocument.isSelected = false;

                return nextDocument;
              });

              result.documents = nextDocuments;

              return result;
            },
          );

          nextDocumentsByCategoryId[item.id] = nextCategory;
        }
      });

      state.documentsByCategoryId = nextDocumentsByCategoryId;
      state.allDocuments = nextAllDocumentsState;

      state.allDocumentsSelected = 0;
      state.selectedDocumentsByGroupId = {};
    },
    deleteIsSuccess: (
      state,
      action: PayloadAction<{ ids: string[]; id?: string }>,
    ) => {
      const nextDocumentsByCategoryId = { ...state.documentsByCategoryId };
      const nextAllDocuments = { ...state.allDocuments };

      const deletedDocuments: CustomerDocument[] = [];

      if (state.allowedCategories && state.documentsByCategoryId) {
        state.allowedCategories.forEach((item) => {
          if (nextDocumentsByCategoryId[item.id]) {
            const nextCategory = nextDocumentsByCategoryId[item.id].map(
              (category) => {
                const result = { ...category };

                result.documents?.forEach((document) => {
                  if (
                    document.isSelected ||
                    action.payload.id === document.id
                  ) {
                    deletedDocuments.push(document);
                  }
                });

                if (action.payload.id) {
                  result.documents = result.documents?.filter(
                    (document) => action.payload.id !== document.id,
                  );
                } else {
                  result.documents = result.documents?.filter(
                    (document) => !document.isSelected,
                  );
                }

                return result;
              },
            );

            nextDocumentsByCategoryId[item.id] = nextCategory;
          }
        });

        state.documentsByCategoryId = nextDocumentsByCategoryId;
      }

      if (state.allDocuments) {
        state.allDocumentsTotal -= action.payload.ids.length;

        const nextCategory = nextAllDocuments.all.map((item) => {
          const result = { ...item };

          result.documents?.forEach((document) => {
            if (
              (document.isSelected || document.id === action.payload.id) &&
              !deletedDocuments.find((deleted) => deleted.id === document.id)
            ) {
              deletedDocuments.push(document);
            }
          });

          if (action.payload.id) {
            result.documents = result.documents?.filter(
              (document) => action.payload.id !== document.id,
            );
          } else {
            result.documents = result.documents?.filter(
              (document) => !document.isSelected,
            );
          }

          return result;
        });

        nextAllDocuments.all = nextCategory;

        state.allDocuments = nextAllDocuments;
      }

      state.allDocumentsSelected = 0;
      state.selectedDocumentsByGroupId = {};
    },
    onSelectAllGroupById: (
      state,
      action: PayloadAction<{
        selected: boolean;
        id: string;
      }>,
    ) => {
      const { selected, id } = action.payload;

      if (id === 'all') {
        const nextAllDocuments = { ...state.allDocuments };
        const nextDocumentsByCategoryId = { ...state.documentsByCategoryId };
        const nextDocumentsSelectedByCategoryId = {
          ...state.selectedDocumentsByGroupId,
        };

        const nextCategory = nextAllDocuments.all.map((item) => {
          const result = { ...item };

          const nextDocuments = result.documents?.map((document) => {
            const nextDocument = { ...document };

            nextDocument.isSelected = selected;

            if (
              document.documentCategoryId &&
              nextDocumentsByCategoryId[document.documentCategoryId]
            ) {
              const nextCategoryById = nextDocumentsByCategoryId[
                document.documentCategoryId
              ].map((categoryById) => {
                const nextResult = { ...categoryById };

                const nextDocumentsInCategory = nextResult.documents?.map(
                  (documentInCategory) => {
                    const nextDocumentInCategory = { ...documentInCategory };

                    nextDocumentInCategory.isSelected = selected;

                    return nextDocumentInCategory;
                  },
                );

                nextResult.documents = nextDocumentsInCategory;

                return nextResult;
              });

              let counter = 0;

              if (selected) {
                nextCategoryById?.forEach((category) => {
                  category?.documents?.forEach((categoryDocument) => {
                    if (categoryDocument.isSelected) {
                      counter += 1;
                    }
                  });
                });
              }

              nextDocumentsSelectedByCategoryId[document.documentCategoryId] =
                counter;
              nextDocumentsByCategoryId[document.documentCategoryId] =
                nextCategoryById;
            }

            return nextDocument;
          });

          result.documents = nextDocuments;

          return result;
        });

        nextAllDocuments.all = nextCategory;

        let counterAll = 0;

        if (selected) {
          nextAllDocuments.all?.forEach((item) => {
            item.documents?.forEach((document) => {
              if (document.isSelected) {
                counterAll += 1;
              }
            });
          });
        } else {
          counterAll = 0;
        }

        if (selected) {
          state.selectedDocumentsByGroupId = nextDocumentsSelectedByCategoryId;
        } else {
          state.selectedDocumentsByGroupId = {};
        }

        state.allDocumentsSelected = counterAll;

        state.documentsByCategoryId = nextDocumentsByCategoryId;
        state.allDocuments = nextAllDocuments;
      } else if (id !== 'all') {
        const nextDocumentsByCategoryId = { ...state.documentsByCategoryId };
        const nextDocumentsSelectedByCategoryId = {
          ...state.selectedDocumentsByGroupId,
        };

        const nextAllDocumentsState = { ...state.allDocuments };

        let counterAll = 0;

        if (nextDocumentsByCategoryId[id]) {
          let counter = 0;

          const nextCategoryById = nextDocumentsByCategoryId[id]?.map(
            (item) => {
              const result = { ...item };

              const nextDocuments = result.documents?.map((document) => {
                const nextDocument = { ...document };

                nextDocument.isSelected = selected;

                if (state.allDocuments) {
                  const nextAllDocuments = nextAllDocumentsState?.all?.map(
                    (allDocuments) => {
                      const nextResult = { ...allDocuments };

                      const nextAllCategory = nextResult.documents?.map(
                        (allDocument) => {
                          const nextAllDocument = { ...allDocument };

                          if (nextAllDocument.id === nextDocument.id) {
                            nextAllDocument.isSelected = selected;
                          }

                          return nextAllDocument;
                        },
                      );

                      nextResult.documents = nextAllCategory;

                      return nextResult;
                    },
                  );

                  nextAllDocumentsState.all = nextAllDocuments;
                }

                return nextDocument;
              });

              result.documents = nextDocuments;

              return result;
            },
          );

          if (state.allDocuments) {
            nextAllDocumentsState?.all?.forEach((item) => {
              item?.documents?.forEach((document) => {
                if (document.isSelected) {
                  counterAll += 1;
                }
              });
            });
          }

          if (selected) {
            nextCategoryById.forEach((item) => {
              item.documents?.forEach((document) => {
                if (document.isSelected) {
                  counter += 1;
                }
              });
            });

            nextDocumentsSelectedByCategoryId[id] = counter;
          } else {
            nextDocumentsSelectedByCategoryId[id] = 0;
          }

          nextDocumentsByCategoryId[id] = nextCategoryById;

          state.selectedDocumentsByGroupId = nextDocumentsSelectedByCategoryId;
          state.documentsByCategoryId = nextDocumentsByCategoryId;
          state.allDocuments = nextAllDocumentsState;
          state.allDocumentsSelected = counterAll;
        }
      }
    },
    onSelectAllGroup: (
      state,
      action: PayloadAction<{
        selected: boolean;
        type?: string;
      }>,
    ) => {
      const { selected, type } = action.payload;

      if (type === 'all') {
        const nextAllDocuments = { ...state.allDocuments };
        const nextDocumentsByCategoryId = { ...state.documentsByCategoryId };
        const nextDocumentsSelectedByCategoryId = {
          ...state.selectedDocumentsByGroupId,
        };

        const nextCategory = nextAllDocuments.all.map((item) => {
          const result = { ...item };

          const nextDocuments = result.documents?.map((document) => {
            const nextDocument = { ...document };

            nextDocument.isSelected = selected;

            if (
              document.documentCategoryId &&
              nextDocumentsByCategoryId[document.documentCategoryId]
            ) {
              const nextCategoryById = nextDocumentsByCategoryId[
                document.documentCategoryId
              ].map((categoryById) => {
                const nextResult = { ...categoryById };

                const nextDocumentsInCategory = nextResult.documents?.map(
                  (documentInCategory) => {
                    const nextDocumentInCategory = { ...documentInCategory };

                    nextDocumentInCategory.isSelected = selected;

                    return nextDocumentInCategory;
                  },
                );

                nextResult.documents = nextDocumentsInCategory;

                return nextResult;
              });

              let counter = 0;

              if (selected) {
                nextCategoryById?.forEach((category) => {
                  category?.documents?.forEach((categoryDocument) => {
                    if (categoryDocument.isSelected) {
                      counter += 1;
                    }
                  });
                });
              }

              nextDocumentsSelectedByCategoryId[document.documentCategoryId] =
                counter;
              nextDocumentsByCategoryId[document.documentCategoryId] =
                nextCategoryById;
            }

            return nextDocument;
          });

          result.documents = nextDocuments;

          return result;
        });

        nextAllDocuments.all = nextCategory;

        let counterAll = 0;

        if (selected) {
          nextAllDocuments.all?.forEach((item) => {
            item.documents?.forEach((document) => {
              if (document.isSelected) {
                counterAll += 1;
              }
            });
          });
        } else {
          counterAll = 0;
        }

        if (selected) {
          state.selectedDocumentsByGroupId = nextDocumentsSelectedByCategoryId;
        } else {
          state.selectedDocumentsByGroupId = {};
        }

        state.allDocumentsSelected = counterAll;

        state.documentsByCategoryId = nextDocumentsByCategoryId;
        state.allDocuments = nextAllDocuments;
      } else if (type !== 'all' && state.allowedCategories) {
        const currentCategory = state.allowedCategories.find(
          (item) => item.category === type || item.id === type,
        );

        if (currentCategory) {
          const nextDocumentsByCategoryId = { ...state.documentsByCategoryId };
          const nextDocumentsSelectedByCategoryId = {
            ...state.selectedDocumentsByGroupId,
          };

          const nextAllDocumentsState = { ...state.allDocuments };

          let counterAll = 0;

          if (nextDocumentsByCategoryId[currentCategory.id]) {
            let counter = 0;

            const nextCategoryById = nextDocumentsByCategoryId[
              currentCategory.id
            ]?.map((item) => {
              const result = { ...item };

              const nextDocuments = result.documents?.map((document) => {
                const nextDocument = { ...document };

                nextDocument.isSelected = selected;

                if (state.allDocuments) {
                  const nextAllDocuments = nextAllDocumentsState?.all?.map(
                    (allDocuments) => {
                      const nextResult = { ...allDocuments };

                      const nextAllCategory = nextResult.documents?.map(
                        (allDocument) => {
                          const nextAllDocument = { ...allDocument };

                          if (nextAllDocument.id === nextDocument.id) {
                            nextAllDocument.isSelected = selected;
                          }

                          return nextAllDocument;
                        },
                      );

                      nextResult.documents = nextAllCategory;

                      return nextResult;
                    },
                  );

                  nextAllDocumentsState.all = nextAllDocuments;
                }

                return nextDocument;
              });

              result.documents = nextDocuments;

              return result;
            });

            if (state.allDocuments) {
              nextAllDocumentsState?.all?.forEach((item) => {
                item.documents?.forEach((document) => {
                  if (document.isSelected) {
                    counterAll += 1;
                  }
                });
              });
            }

            if (selected) {
              nextCategoryById.forEach((item) => {
                item.documents?.forEach((document) => {
                  if (document.isSelected) {
                    counter += 1;
                  }
                });
              });

              nextDocumentsSelectedByCategoryId[currentCategory.id] = counter;
            } else {
              nextDocumentsSelectedByCategoryId[currentCategory.id] = 0;
            }

            nextDocumentsByCategoryId[currentCategory.id] = nextCategoryById;

            state.selectedDocumentsByGroupId =
              nextDocumentsSelectedByCategoryId;
            state.documentsByCategoryId = nextDocumentsByCategoryId;
            state.allDocuments = nextAllDocumentsState;
            state.allDocumentsSelected = counterAll;
          }
        }
      }
    },
    onDocumentSearch: (state, action: PayloadAction<string>) => {
      state.searchText = action.payload;
    },
    onDocumentRaname: (
      state,
      action: PayloadAction<{ name: string; type: string; documentId: string }>,
    ) => {
      if (state.documentsByCategoryId) {
        const nextDocumentsByCategoryId = { ...state.documentsByCategoryId };

        if (nextDocumentsByCategoryId[action.payload.type]) {
          const nextCategory = nextDocumentsByCategoryId[
            action.payload.type
          ]?.map((item) => {
            const result = { ...item };

            const nextDocuments = result.documents?.map((document) => {
              const nextDocument = { ...document };

              if (nextDocument.id === action.payload.documentId) {
                nextDocument.name = action.payload.name;
              }

              return nextDocument;
            });

            result.documents = nextDocuments;

            return result;
          });

          nextDocumentsByCategoryId[action.payload.type] = nextCategory;

          state.documentsByCategoryId = nextDocumentsByCategoryId;
        }
      }

      if (state.allDocuments) {
        const nextAllDocuments = { ...state.allDocuments };

        const nextCategory = nextAllDocuments?.all?.map((item) => {
          const result = { ...item };

          const nextDocuments = result?.documents?.map((document) => {
            const nextDocument = { ...document };

            if (nextDocument.id === action.payload.documentId) {
              nextDocument.name = action.payload.name;
            }

            return nextDocument;
          });

          result.documents = nextDocuments;

          return result;
        });

        nextAllDocuments.all = nextCategory;

        state.allDocuments = nextAllDocuments;
      }
    },
    onDocumentsMoveById: (
      state,
      action: PayloadAction<{
        documentCategoryId: string;
        ids?: string[];
        type?: string;
        id?: string;
      }>,
    ) => {
      if (state.allowedCategories) {
        const nextDocumentsByCategoryId = { ...state.documentsByCategoryId };
        const movedDocuments: CustomerDocument[] = [];

        state.allowedCategories.forEach((category) => {
          if (
            state.documentsByCategoryId &&
            nextDocumentsByCategoryId[category.id]
          ) {
            nextDocumentsByCategoryId[category.id].forEach((categoryId) => {
              categoryId?.documents?.forEach((document) => {
                if (document.isSelected) {
                  movedDocuments.push({
                    ...document,
                    isSelected: false,
                    category: undefined,
                    documentCategoryId: action.payload.documentCategoryId,
                    prevDocumentCategoryId: document.documentCategoryId,
                  });
                }
              });
            });

            const nextCategory = nextDocumentsByCategoryId[category.id].map(
              (categoryId) => {
                const result = { ...categoryId };

                if (action.payload.id) {
                  const nextDocuments = result.documents?.filter(
                    (document) => document.id !== action.payload.id,
                  );

                  result.documents = nextDocuments;
                } else {
                  const nextDocuments = result.documents?.filter(
                    (document) => !document.isSelected,
                  );

                  result.documents = nextDocuments;
                }

                return result;
              },
            );

            nextDocumentsByCategoryId[category.id] = nextCategory;
          }

          if (state.allDocuments) {
            const nextAllDocumentsState = { ...state.allDocuments };

            const nextAllDocuments = nextAllDocumentsState.all.map((item) => {
              const result = { ...item };

              const nextDocuments = result.documents?.map((document) => {
                const nextDocument = { ...document };

                if (nextDocument.isSelected) {
                  if (
                    !movedDocuments.find(
                      (movedDocument) => movedDocument.id === nextDocument.id,
                    )
                  ) {
                    movedDocuments.push({
                      ...document,
                      isSelected: false,
                      category: undefined,
                      documentCategoryId: action.payload.documentCategoryId,
                      prevDocumentCategoryId: document.documentCategoryId,
                    });
                  }

                  nextDocument.isSelected = false;
                  nextDocument.category = undefined;
                  nextDocument.documentCategoryId =
                    action.payload.documentCategoryId;
                }

                return nextDocument;
              });

              result.documents = nextDocuments;

              return result;
            });

            nextAllDocumentsState.all = nextAllDocuments;

            state.allDocuments = nextAllDocumentsState;
          }
        });

        if (
          state.documentsByCategoryId &&
          nextDocumentsByCategoryId[action.payload.documentCategoryId] &&
          action.payload.documentCategoryId !== action.payload?.type
        ) {
          nextDocumentsByCategoryId[action.payload.documentCategoryId] = [];
        }

        state.documentsByCategoryId = nextDocumentsByCategoryId;
        state.selectedDocumentsByGroupId = {};
        state.allDocumentsSelected = 0;
      }
    },
    setBlockView: (state, action: PayloadAction<boolean>) => {
      state.blockView = action.payload;
    },
    sendMessage: (state, action: PayloadAction<string>) => {
      const nextDocument = { ...state.info };

      nextDocument.warningMessage = action.payload;

      if (state.allowedCategories && state.documentsByCategoryId) {
        const nextDocumentsByCategoryId = { ...state.documentsByCategoryId };

        state.allowedCategories.forEach((item) => {
          if (nextDocumentsByCategoryId[item.id]) {
            const nextCategory = nextDocumentsByCategoryId[item.id].map(
              (category) => {
                const result = { ...category };

                const nextDocuments = result.documents?.map((documentItem) => {
                  const nextDocumentItem = { ...documentItem };

                  if (nextDocumentItem.id === nextDocument.id) {
                    nextDocumentItem.warningMessage = action.payload;
                  }

                  return nextDocumentItem;
                });

                result.documents = nextDocuments;

                return result;
              },
            );

            nextDocumentsByCategoryId[item.id] = nextCategory;
          }
        });

        state.documentsByCategoryId = nextDocumentsByCategoryId;
      }

      if (state.allDocuments) {
        const nextAllDocuments = { ...state.allDocuments };

        const nextCategory = nextAllDocuments.all.map((category) => {
          const result = { ...category };

          const nextDocuments = result.documents?.map((documentItem) => {
            const nextDocumentItem = { ...documentItem };

            if (nextDocumentItem.id === nextDocument.id) {
              nextDocumentItem.warningMessage = action.payload;
            }

            return nextDocumentItem;
          });

          result.documents = nextDocuments;

          return result;
        });

        nextAllDocuments.all = nextCategory;

        state.allDocuments = nextAllDocuments;
      }

      state.info = nextDocument as CustomerDocument;
    },
    setNeedToReloadCategoryId: (state, action: PayloadAction<string>) => {
      state.needToReloadCategoryId = action.payload;
    },
    clearDocumentsState: (state) => {
      state.selectedDocumentsCount = 0;
      state.identificationDocumentsSelected = 0;
      state.transactionDocumentsSelected = 0;
      state.pspCustomerDocumentsSelected = 0;
      state.cddCheckDocumentsSelected = 0;
      state.otherDocumentsSelected = 0;

      state.allSelected = false;
      state.searchText = '';

      state.deleteSuccess = undefined;
      state.customerId = undefined;
      state.customerType = undefined;
      state.stats = undefined;
      state.needToReloadCategoryId = undefined;
      state.cursor = undefined;
      state.cursorByCategoryId = undefined;
      state.documentsByCategoryId = undefined;
      state.allDocuments = undefined;
      state.expiredDocuments = undefined;
      state.expiredDocumentsPagination = undefined;
    },
    clearAgreementState: (state) => {
      state.agreement = undefined;
    },
    clearDocumentState: (state) => {
      state.info = undefined;
      state.documentId = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      customersComponentApi.endpoints.getExpiredDocuments.matchFulfilled,
      (state, action: PayloadAction<Response<CustomerDocument>>) => {
        state.expiredDocuments = action.payload.data.edges.map(
          (item) => item.node,
        );
        state.expiredDocumentsPagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.renameCustomerDocument.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerDocument }>) => {
        state.expiredDocuments = state.expiredDocuments?.map((item) => {
          const result = { ...item };

          if (result.id === action.payload.data.id) {
            result.name = action.payload.data.name;
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.deleteCustomerDocument.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerDocument }>) => {
        state.expiredDocuments = state?.expiredDocuments?.filter(
          (item) => item.id !== action.payload.data.id,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerDocuments.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<CustomerDocument>> & {
          meta: {
            arg: {
              originalArgs: {
                categoryId?: string;
                cursor?: string;
                take?: string;
                name?: string;
                status?: string[];
                reload?: boolean;
              };
            };
          };
        },
      ) => {
        if (action.meta.arg.originalArgs?.categoryId) {
          const nextSelectedDocumentsyCategoryId = {
            ...state.selectedDocumentsByGroupId,
          };

          let nextResponse = action.payload.data.edges.map((item) => item.node);

          if (state.allDocuments && state.allDocuments.all) {
            nextResponse = nextResponse.map((item) => {
              const result = { ...item };

              state.allDocuments?.all?.forEach((category) => {
                category.documents?.forEach((document) => {
                  if (document.id === result.id && document.isSelected) {
                    result.isSelected = true;
                  }
                });
              });

              return result;
            });

            if (
              nextSelectedDocumentsyCategoryId[
                action.meta.arg.originalArgs?.categoryId
              ]
            ) {
              // eslint-disable-next-line operator-assignment
              nextSelectedDocumentsyCategoryId[
                action.meta.arg.originalArgs?.categoryId
              ] =
                nextSelectedDocumentsyCategoryId[
                  action.meta.arg.originalArgs?.categoryId
                ] +
                (nextResponse?.filter((item) => item.isSelected)?.length || 0);
            } else {
              nextSelectedDocumentsyCategoryId[
                action.meta.arg.originalArgs?.categoryId
              ] = nextResponse?.filter((item) => item.isSelected)?.length;
            }

            state.selectedDocumentsByGroupId = nextSelectedDocumentsyCategoryId;
          }

          if (!action.meta.arg.originalArgs?.reload) {
            if (!state.documentsByCategoryId) {
              state.documentsByCategoryId = {
                [action.meta.arg.originalArgs?.categoryId]: [
                  {
                    startCursor: action.payload.data.pageInfo.startCursor,
                    endCursor: action.payload.data.pageInfo.endCursor,
                    hasNext: action.payload.data.pageInfo.hasNextPage,
                    hasPrev: action.payload.data.pageInfo.hasPreviousPage,
                    documents: nextResponse,
                  },
                ],
              };
            } else {
              const nextDocumentsByCategoryId = {
                ...state.documentsByCategoryId,
              };

              if (
                !action?.meta?.arg?.originalArgs?.cursor ||
                (action?.meta?.arg?.originalArgs?.cursor &&
                  !state.documentsByCategoryId)
              ) {
                nextDocumentsByCategoryId[
                  action.meta.arg.originalArgs?.categoryId
                ] = [
                  {
                    startCursor: action.payload.data.pageInfo.startCursor,
                    endCursor: action.payload.data.pageInfo.endCursor,
                    hasNext: action.payload.data.pageInfo.hasNextPage,
                    hasPrev: action.payload.data.pageInfo.hasPreviousPage,
                    documents: nextResponse,
                  },
                ];
              } else {
                const nextCategory = ([] as DocumentsCategoryState[]).concat(
                  nextDocumentsByCategoryId[
                    action.meta.arg.originalArgs?.categoryId
                  ],
                );

                nextCategory.push({
                  startCursor: action.payload.data.pageInfo.startCursor,
                  endCursor: action.payload.data.pageInfo.endCursor,
                  hasNext: action.payload.data.pageInfo.hasNextPage,
                  hasPrev: action.payload.data.pageInfo.hasPreviousPage,
                  documents: nextResponse,
                  prevEndCursor:
                    Number(action?.meta?.arg?.originalArgs?.take) > 0
                      ? action?.meta?.arg?.originalArgs?.cursor
                      : '',
                  prevStartCursor:
                    Number(action?.meta?.arg?.originalArgs?.take) < 0
                      ? action?.meta?.arg?.originalArgs?.cursor
                      : '',
                });

                const formattedNextCategory = nextCategory?.map((item) => {
                  const result = { ...item };

                  if (Number(action?.meta?.arg?.originalArgs?.take) > 0) {
                    if (
                      result.endCursor ===
                      action?.meta?.arg?.originalArgs?.cursor
                    ) {
                      result.prevStartCursor =
                        action.payload.data.pageInfo.startCursor;
                    }
                  } else if (
                    Number(action?.meta?.arg?.originalArgs?.take) < 0
                  ) {
                    if (
                      result.startCursor ===
                      action?.meta?.arg?.originalArgs?.cursor
                    ) {
                      result.prevEndCursor =
                        action.payload.data.pageInfo.endCursor;
                    }
                  }

                  return result;
                });

                nextDocumentsByCategoryId[
                  action.meta.arg.originalArgs?.categoryId
                ] = formattedNextCategory;
              }

              state.documentsByCategoryId = nextDocumentsByCategoryId;
            }
          } else if (
            action.meta.arg.originalArgs?.reload ||
            (action.meta.arg.originalArgs?.name &&
              action.meta.arg.originalArgs?.reload) ||
            (action.meta.arg.originalArgs?.reload &&
              action.meta.arg.originalArgs?.status)
          ) {
            if (state.needToReloadCategoryId) {
              const nextDocumentsByCategoryId = {
                ...state.documentsByCategoryId,
              };

              nextDocumentsByCategoryId[state.needToReloadCategoryId] = [
                {
                  startCursor: action.payload.data.pageInfo.startCursor,
                  endCursor: action.payload.data.pageInfo.endCursor,
                  hasNext: action.payload.data.pageInfo.hasNextPage,
                  hasPrev: action.payload.data.pageInfo.hasPreviousPage,
                  documents: nextResponse,
                },
              ];

              state.documentsByCategoryId = nextDocumentsByCategoryId;
              state.needToReloadCategoryId = '';
            } else {
              state.documentsByCategoryId = {
                [action.meta.arg.originalArgs?.categoryId]: [
                  {
                    startCursor: action.payload.data.pageInfo.startCursor,
                    endCursor: action.payload.data.pageInfo.endCursor,
                    hasNext: action.payload.data.pageInfo.hasNextPage,
                    hasPrev: action.payload.data.pageInfo.hasPreviousPage,
                    documents: nextResponse,
                  },
                ],
              };
            }

            if (action.meta.arg.originalArgs?.reload) {
              const nextCursorByCategoryId = { ...state.cursorByCategoryId };

              nextCursorByCategoryId[action.meta.arg.originalArgs?.categoryId] =
                {
                  startCursor: action.payload.data.pageInfo.startCursor,
                  endCursor: action.payload.data.pageInfo.endCursor,
                  take: '20',
                };

              state.cursorByCategoryId = {
                ...state.cursorByCategoryId,
                [action.meta.arg.originalArgs?.categoryId]: {
                  startCursor: action.payload.data.pageInfo.startCursor,
                  endCursor: action.payload.data.pageInfo.endCursor,
                  take: '20',
                },
              };

              if (state.allDocuments) {
                state.allDocuments = undefined;
              }
            }
          }
        } else {
          state.allDocumentsTotal = action.payload.data.pageInfo.totalCount;

          let nextAllResponse = action.payload.data.edges.map(
            (item) => item.node,
          );

          if (state.documentsByCategoryId && state.allowedCategories) {
            nextAllResponse = nextAllResponse?.map((value) => {
              const result = { ...value };

              state.allowedCategories?.forEach((item) => {
                if (
                  state.documentsByCategoryId &&
                  state.documentsByCategoryId[item.id]
                ) {
                  state.documentsByCategoryId[item.id]?.forEach((category) => {
                    category.documents?.forEach((document) => {
                      if (document.id === result.id && document.isSelected) {
                        result.isSelected = true;
                      }
                    });
                  });
                }
              });

              return result;
            });
          }

          if (!action.meta.arg.originalArgs?.reload) {
            if (
              !action.meta.arg.originalArgs.cursor ||
              (action.meta.arg.originalArgs.cursor && !state.allDocuments)
            ) {
              state.allDocuments = {
                all: [
                  {
                    startCursor: action.payload.data.pageInfo.startCursor,
                    endCursor: action.payload.data.pageInfo.endCursor,
                    hasNext: action.payload.data.pageInfo.hasNextPage,
                    hasPrev: action.payload.data.pageInfo.hasPreviousPage,
                    documents: nextAllResponse,
                  },
                ],
              };

              state.cursor = {
                startCursor: action.payload.data.pageInfo.startCursor,
                endCursor: action.payload.data.pageInfo.endCursor,
                take: '20',
              };
            } else {
              const nextAllDocuments = { ...state.allDocuments };
              const nextCategory = ([] as DocumentsCategoryState[]).concat(
                nextAllDocuments.all,
              );

              nextCategory.push({
                startCursor: action.payload.data.pageInfo.startCursor,
                endCursor: action.payload.data.pageInfo.endCursor,
                hasNext: action.payload.data.pageInfo.hasNextPage,
                hasPrev: action.payload.data.pageInfo.hasPreviousPage,
                documents: nextAllResponse,
                prevEndCursor:
                  Number(action?.meta?.arg?.originalArgs?.take) > 0
                    ? action?.meta?.arg?.originalArgs?.cursor
                    : '',
                prevStartCursor:
                  Number(action?.meta?.arg?.originalArgs?.take) < 0
                    ? action?.meta?.arg?.originalArgs?.cursor
                    : '',
              });

              const formattedNextCategory = nextCategory?.map((item) => {
                const result = { ...item };

                if (Number(action?.meta?.arg?.originalArgs?.take) > 0) {
                  if (
                    result.endCursor === action?.meta?.arg?.originalArgs?.cursor
                  ) {
                    result.prevStartCursor =
                      action.payload.data.pageInfo.startCursor;
                  }
                } else if (Number(action?.meta?.arg?.originalArgs?.take) < 0) {
                  if (
                    result.startCursor ===
                    action?.meta?.arg?.originalArgs?.cursor
                  ) {
                    result.prevEndCursor =
                      action.payload.data.pageInfo.endCursor;
                  }
                }

                return result;
              });

              nextAllDocuments.all = formattedNextCategory;

              state.allDocuments = nextAllDocuments;
            }
          } else if (
            action.meta.arg.originalArgs?.reload ||
            (action.meta.arg.originalArgs.name &&
              action.meta.arg.originalArgs?.reload) ||
            (action.meta.arg.originalArgs?.reload &&
              action.meta.arg.originalArgs?.status)
          ) {
            state.allDocuments = {
              all: [
                {
                  startCursor: action.payload.data.pageInfo.startCursor,
                  endCursor: action.payload.data.pageInfo.endCursor,
                  hasNext: action.payload.data.pageInfo.hasNextPage,
                  hasPrev: action.payload.data.pageInfo.hasPreviousPage,
                  documents: nextAllResponse,
                },
              ],
            };

            if (action.meta.arg.originalArgs?.reload) {
              state.cursor = {
                startCursor: action.payload.data.pageInfo.startCursor,
                endCursor: action.payload.data.pageInfo.endCursor,
                take: '20',
              };
            }
          }

          if (state.allDocumentsSelected) {
            state.allDocumentsSelected +=
              nextAllResponse.filter((item) => item.isSelected)?.length || 0;
          } else {
            state.allDocumentsSelected = nextAllResponse.filter(
              (item) => item.isSelected,
            )?.length;
          }
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerAgreements.matchFulfilled,
      (state, action: PayloadAction<Response<CustomerAgreement>>) => {
        state.agreements = action.payload.data.edges.map((item) => item.node);
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerOrganizationDocumentsStats
        .matchFulfilled,
      (state, action: PayloadAction<{ data: DocumentsStats[] }>) => {
        state.stats = action.payload.data;

        const nextDocumentsByCategoryIdTotal: Record<string, number> = {};

        action.payload.data.forEach((item) => {
          nextDocumentsByCategoryIdTotal[item.documentCategoryId] = item.count;
        });

        state.documentsByCategoryTotal = nextDocumentsByCategoryIdTotal;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerUnsignedAgreements
        .matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerAgreement[] }>) => {
        state.usignedAgreements = action.payload.data.map((item) => item);
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerDocument.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerDocument }>) => {
        state.documentId = action.payload.data.id;

        state.info = {
          ...action.payload.data,
          commentsList: action.payload.data.comments?.edges.map(
            (item) => item.node,
          ),
        };
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerAgreement.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerAgreement }>) => {
        state.agreement = action.payload.data;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints
        .getCustomerOrganizationAllowedDocumentsCategories.matchFulfilled,
      (state, action: PayloadAction<{ data: AllowedCategory[] }>) => {
        state.allowedCategories = action.payload.data;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.changeDocumentExpirationDate
        .matchFulfilled,
      (state, action: PayloadAction<{ data: Document }>) => {
        if (state.info) {
          const nextInfo = { ...state?.info };

          nextInfo.expirationDate = action.payload.data.expirationDate;

          state.info = nextInfo;
        }

        if (state.allowedCategories && state.documentsByCategoryId) {
          const nextDocumentsByCategoryId = { ...state.documentsByCategoryId };

          state.allowedCategories.forEach((item) => {
            if (nextDocumentsByCategoryId[item.id]) {
              const nextCategory = nextDocumentsByCategoryId[item.id].map(
                (category) => {
                  const result = { ...category };

                  const nextDocuments = result.documents?.map((document) => {
                    const nextDocument = { ...document };

                    if (nextDocument.id === action.payload.data.id) {
                      nextDocument.expirationDate =
                        action.payload.data.expirationDate;
                    }

                    return nextDocument;
                  });

                  result.documents = nextDocuments;

                  return result;
                },
              );

              nextDocumentsByCategoryId[item.id] = nextCategory;
            }
          });

          state.documentsByCategoryId = nextDocumentsByCategoryId;
        }

        if (state.allDocuments) {
          const nextAllDocuments = { ...state.allDocuments };
          const nextCategory = nextAllDocuments.all.map((category) => {
            const result = { ...category };

            const nextDocuments = result.documents?.map((document) => {
              const nextDocument = { ...document };

              if (nextDocument.id === action.payload.data.id) {
                nextDocument.expirationDate =
                  action.payload.data.expirationDate;
              }

              return nextDocument;
            });

            result.documents = nextDocuments;

            return result;
          });

          nextAllDocuments.all = nextCategory;

          state.allDocuments = nextAllDocuments;
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.changeCustomerDocumentsStatus
        .matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                body: {
                  ids: string[];
                  status: CustomerDocumentStatus;
                };
              };
            };
          };
        },
      ) => {
        const { ids, status } = action.meta.arg.originalArgs.body;

        if (state.documentsByCategoryId && state.allowedCategories) {
          const nextDocumentsByCategoryId = { ...state.documentsByCategoryId };

          state.allowedCategories.forEach((item) => {
            if (nextDocumentsByCategoryId[item.id]) {
              const nextCategory = nextDocumentsByCategoryId[item.id].map(
                (category) => {
                  const result = { ...category };

                  const nextDocuments = result.documents?.map((document) => {
                    const nextDocument = { ...document };

                    const currentUpdatedDocumentId = ids.find(
                      (id) => nextDocument.id === id,
                    );

                    if (currentUpdatedDocumentId) {
                      nextDocument.status = status;
                    }

                    return nextDocument;
                  });

                  result.documents = nextDocuments;

                  return result;
                },
              );

              nextDocumentsByCategoryId[item.id] = nextCategory;
            }
          });

          state.documentsByCategoryId = nextDocumentsByCategoryId;
        }

        if (state.allDocuments) {
          const nextAllDocuments = { ...state.allDocuments };

          const nextCategory = nextAllDocuments.all.map((item) => {
            const result = { ...item };

            const nextDocuments = result.documents?.map((document) => {
              const nextDocument = { ...document };

              const currentUpdatedDocumentId = ids.find(
                (id) => nextDocument.id === id,
              );

              if (currentUpdatedDocumentId) {
                nextDocument.status = status;
              }

              return nextDocument;
            });

            result.documents = nextDocuments;

            return result;
          });

          nextAllDocuments.all = nextCategory;

          state.allDocuments = nextAllDocuments;
        }

        if (state.info) {
          const nextDocument = { ...state.info };

          if (ids.find((item) => item === nextDocument.id)) {
            nextDocument.status = status;

            state.info = nextDocument;
          }
        }
      },
    );
    builder.addMatcher(
      clientsDocumentsApi.endpoints.addCustomersComment.matchFulfilled,
      (state, action: PayloadAction<{ data: DocumentComment }>) => {
        if (state.info) {
          const nextDocument = { ...state.info };
          let nextComments: DocumentComment[] = [];

          if (
            nextDocument.commentsList &&
            nextDocument.commentsList.length > 0
          ) {
            nextComments = ([] as DocumentComment[]).concat(
              nextDocument.commentsList,
            );
          }

          if (state.allowedCategories && state.documentsByCategoryId) {
            const nextDocumentsByCategoryId = {
              ...state.documentsByCategoryId,
            };

            state.allowedCategories.forEach((item) => {
              if (nextDocumentsByCategoryId[item.id]) {
                const nextCategory = nextDocumentsByCategoryId[item.id].map(
                  (category) => {
                    const result = { ...category };

                    const nextDocuments = result?.documents?.map(
                      (documentItem) => {
                        const nextDocumentItem = { ...documentItem };

                        if (nextDocumentItem.id === state?.info?.id) {
                          nextDocumentItem.countComments = {
                            count:
                              (nextDocumentItem.countComments?.count || 0) + 1,
                          };
                        }

                        return nextDocumentItem;
                      },
                    );

                    result.documents = nextDocuments;

                    return result;
                  },
                );

                nextDocumentsByCategoryId[item.id] = nextCategory;
              }
            });

            state.documentsByCategoryId = nextDocumentsByCategoryId;
          }

          if (state.allDocuments) {
            const nextAllDocuments = { ...state.allDocuments };

            const nextCategory = nextAllDocuments.all.map((category) => {
              const result = { ...category };

              const nextDocuments = result?.documents?.map((documentItem) => {
                const nextDocumentItem = { ...documentItem };

                if (nextDocumentItem.id === state?.info?.id) {
                  nextDocumentItem.countComments = {
                    count: (nextDocumentItem.countComments?.count || 0) + 1,
                  };
                }

                return nextDocumentItem;
              });

              result.documents = nextDocuments;

              return result;
            });

            nextAllDocuments.all = nextCategory;

            state.allDocuments = nextAllDocuments;
          }

          nextComments.unshift(action.payload.data);
          nextDocument.commentsList = nextComments;

          state.info = nextDocument;
        }
      },
    );
    builder.addMatcher(
      clientsDocumentsApi.endpoints.editCustomersComment.matchFulfilled,
      (state, action: PayloadAction<{ data: DocumentComment }>) => {
        if (state.info) {
          const nextDocument = { ...state.info };
          nextDocument.commentsList = nextDocument.commentsList?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });

          state.info = nextDocument;
        }
      },
    );
    builder.addMatcher(
      clientsDocumentsApi.endpoints.deleteCustomersComment.matchFulfilled,
      (state, action: PayloadAction<{ data: DocumentComment }>) => {
        if (state.info) {
          const nextDocument = { ...state.info };

          nextDocument.commentsList = nextDocument?.commentsList?.filter(
            (item) => item.id !== action.payload.data.id,
          );

          if (state.allowedCategories && state.documentsByCategoryId) {
            const nextDocumentsByCategoryId = {
              ...state.documentsByCategoryId,
            };

            state.allowedCategories.forEach((item) => {
              if (nextDocumentsByCategoryId[item.id]) {
                const nextCategory = nextDocumentsByCategoryId[item.id].map(
                  (category) => {
                    const result = { ...category };

                    const nextDocuments = result.documents?.map(
                      (documentItem) => {
                        const nextDocumentItem = { ...documentItem };

                        if (nextDocumentItem.id === state?.info?.id) {
                          nextDocumentItem.countComments = {
                            count:
                              (nextDocumentItem.countComments
                                ?.count as number) - 1,
                          };
                        }

                        return nextDocumentItem;
                      },
                    );

                    result.documents = nextDocuments;

                    return result;
                  },
                );

                nextDocumentsByCategoryId[item.id] = nextCategory;
              }
            });

            state.documentsByCategoryId = nextDocumentsByCategoryId;
          }

          if (state.allDocuments) {
            const nextAllDocuments = { ...state.allDocuments };

            const nextCategory = nextAllDocuments.all.map((category) => {
              const result = { ...category };

              const nextDocuments = result.documents?.map((documentItem) => {
                const nextDocumentItem = { ...documentItem };

                if (nextDocumentItem.id === state?.info?.id) {
                  nextDocumentItem.countComments = {
                    count:
                      (nextDocumentItem.countComments?.count as number) - 1,
                  };
                }

                return nextDocumentItem;
              });

              result.documents = nextDocuments;

              return result;
            });

            nextAllDocuments.all = nextCategory;

            state.allDocuments = nextAllDocuments;
          }

          state.info = nextDocument;
        }
      },
    );
  },
});

export const {
  clearDocumentsState,
  onSelectDocument,
  onDocumentSearch,
  onDocumentsMoveById,
  setBlockView,
  onSelectAllGroup,
  clearSelectedState,
  deleteIsSuccess,
  clearDocumentState,
  sendMessage,
  clearAgreementState,
  onSelectAllGroupById,
  onSelectDocumentById,
  onDocumentRaname,
  setNeedToReloadCategoryId,
  setCursor,
  setCursorByCategoryId,
  clearCursor,
  clearCursorByCategoryId,
  clearAllCursors,
} = customerDocumentsSlice.actions;

export default customerDocumentsSlice.reducer;
